import { api } from './client';
import type {
  StrategyTemplateListResponse,
  StrategyTemplate,
  ActiveStrategyDto,
  CreateStrategyRequest,
  UpdateStrategyRequest,
  SetActiveStrategyRequest,
} from '../types/strategy';

function basePath(worldId: string, provinceId: string): string {
  return `/api/worlds/${worldId}/provinces/${provinceId}/strategies`;
}

/**
 * List all custom templates + active state
 * GET /api/worlds/{worldId}/provinces/{provinceId}/strategies
 */
export async function listTemplates(worldId: string, provinceId: string): Promise<StrategyTemplateListResponse> {
  const response = await api.get<StrategyTemplateListResponse>(basePath(worldId, provinceId));
  if (response.error) {
    throw new Error(response.error);
  }
  return response.data!;
}

/**
 * Create custom template
 * POST /api/worlds/{worldId}/provinces/{provinceId}/strategies
 */
export async function createTemplate(
  worldId: string, provinceId: string, request: CreateStrategyRequest
): Promise<StrategyTemplate> {
  const response = await api.post<StrategyTemplate>(basePath(worldId, provinceId), request);
  if (response.error) {
    throw new Error(response.error);
  }
  return response.data!;
}

/**
 * Update custom template
 * PUT /api/worlds/{worldId}/provinces/{provinceId}/strategies/{strategyId}
 */
export async function updateTemplate(
  worldId: string, provinceId: string, strategyId: string, request: UpdateStrategyRequest
): Promise<StrategyTemplate> {
  const response = await api.put<StrategyTemplate>(`${basePath(worldId, provinceId)}/${strategyId}`, request);
  if (response.error) {
    throw new Error(response.error);
  }
  return response.data!;
}

/**
 * Delete custom template
 * DELETE /api/worlds/{worldId}/provinces/{provinceId}/strategies/{strategyId}
 */
export async function deleteTemplate(worldId: string, provinceId: string, strategyId: string): Promise<void> {
  const response = await api.delete(basePath(worldId, provinceId) + '/' + strategyId);
  if (response.error) {
    throw new Error(response.error);
  }
}

/**
 * Get current active strategy (resolved)
 * GET /api/worlds/{worldId}/provinces/{provinceId}/strategies/active
 * Returns null if no active strategy (204)
 */
export async function getActiveStrategy(worldId: string, provinceId: string): Promise<ActiveStrategyDto | null> {
  const response = await api.get<ActiveStrategyDto>(`${basePath(worldId, provinceId)}/active`);
  if (response.error) {
    throw new Error(response.error);
  }
  // 204 No Content returns null data
  return response.data ?? null;
}

/**
 * Set active strategy
 * PUT /api/worlds/{worldId}/provinces/{provinceId}/strategies/active
 */
export async function setActiveStrategy(
  worldId: string, provinceId: string, request: SetActiveStrategyRequest
): Promise<ActiveStrategyDto> {
  const response = await api.put<ActiveStrategyDto>(`${basePath(worldId, provinceId)}/active`, request);
  if (response.error) {
    throw new Error(response.error);
  }
  return response.data!;
}

/**
 * Deactivate current strategy
 * DELETE /api/worlds/{worldId}/provinces/{provinceId}/strategies/active
 */
export async function deactivateStrategy(worldId: string, provinceId: string): Promise<void> {
  const response = await api.delete(`${basePath(worldId, provinceId)}/active`);
  if (response.error) {
    throw new Error(response.error);
  }
}
